<template>
  <div class="gs-app gs-video-analytics">
    <button type="button" class="gs-video-analytics__back" @click="goBack()">
      <gs-icon name="arrow-left" size="14" />
      <span>All analytics</span>
    </button>

    <gs-card class="gs-video-analytics__hero">
      <div class="gs-video-analytics__hero-media" :style="thumbStyle" role="img" :aria-label="video.name"></div>

      <div class="gs-video-analytics__hero-body">
        <div v-if="!videoLoadFailed" class="gs-video-analytics__hero-badges">
          <gs-badge :variant="video.is_published ? 'success' : 'danger'">
            {{ video.is_published ? 'Published' : 'Draft' }}
          </gs-badge>
          <span v-if="categoryName" class="gs-video-analytics__chip">{{ categoryName }}</span>
          <span class="gs-video-analytics__chip">{{ sourceText }}</span>
        </div>
        <h1 class="gs-video-analytics__title">{{ heroTitle }}</h1>
      </div>

      <gs-button v-if="!videoLoadFailed" variant="outline" class="gs-video-analytics__edit" @click="editVideo()">
        <gs-icon name="edit" size="16" />
        Edit video
      </gs-button>
    </gs-card>

    <gs-card v-if="!videoLoadFailed" class="gs-video-analytics__controls">
      <div class="gs-video-analytics__intro">
        <span class="gs-analytics__eyebrow">Performance</span>
        <h2 class="gs-video-analytics__controls-title">This video's activity</h2>
      </div>

      <date-range-pill :preset="preset" :custom="custom" @change="onRangeChange" />
    </gs-card>

    <analytics-error-card
      v-if="videoLoadFailed"
      message="We could not load this video. It may have been deleted."
      @retry="loadVideo()"
    />
    <analytics-error-card v-else-if="failed" @retry="load()" />

    <template v-else>
      <report-status :report="report" :loading="chartLoading" />
      <div class="gs-video-analytics__stats">
        <stat-card
          v-for="card in cards"
          :key="card.label"
          :label="card.label"
          :value="card.value"
          :note="card.note"
          :icon="card.icon"
          :tone="card.tone"
        />
      </div>

      <traffic-chart
        :series="activeSeries"
        :metric="metric"
        :loading="chartLoading"
        @update:metric="onMetricChange"
      />

      <gs-card class="gs-video-analytics__table-card">
        <div class="gs-video-analytics__table-head">
          <h2 class="gs-video-analytics__table-title">Activity breakdown</h2>
          <gs-button variant="outline" :disabled="chartLoading" @click="exportReport">Export CSV</gs-button>
          <p class="gs-video-analytics__table-subtitle">
            Hours for one day, days for up to 62 days, months for longer ranges. Downloads include watch time in seconds.
          </p>
        </div>

        <daily-breakdown-table :rows="dailyRows" />
      </gs-card>

      <analytics-footnote class="gs-video-analytics__note" />
    </template>
  </div>
</template>

<script>
import Video from '@/models/Video';
import Category from '@/models/Category';
import { sourceLabel } from '@/components/library/VideoCard.vue';
import { buildRange } from '@/utils/analyticsRange';
import {
  fetchChart,
  downloadCsv,
  summaryFrom,
  latestOnly,
  mergeSeries,
  formatNumber,
  buildStatCards,
} from '@/services/analyticsApi';
import ReportStatus from '@/components/analytics/ReportStatus.vue';
import StatCard from '@/components/analytics/StatCard.vue';
import TrafficChart from '@/components/analytics/TrafficChart.vue';
import DateRangePill from '@/components/analytics/DateRangePill.vue';
import DailyBreakdownTable from '@/components/analytics/DailyBreakdownTable.vue';
import AnalyticsErrorCard from '@/components/analytics/AnalyticsErrorCard.vue';
import AnalyticsFootnote from '@/components/analytics/AnalyticsFootnote.vue';
import { flattenCategories } from '@/utils/categories';

export default {
  name: 'VideoAnalytics',
  middleware: 'auth',
  layout: 'default',

  components: {
    ReportStatus,
    StatCard,
    TrafficChart,
    DateRangePill,
    DailyBreakdownTable,
    AnalyticsErrorCard,
    AnalyticsFootnote,
  },

  props: {
    videoId: {
      type: [String, Number],
      required: true,
    },
  },

  metaInfo() {
    return { title: 'Video Analytics' };
  },

  data() {
    return {
      preset: 'last30',
      custom: null,
      metric: 'plays',
      video: {},
      videoLoadFailed: false,
      categoryList: [],
      summary: summaryFrom(),
      report: {},
      seriesData: { plays: [], visitors: [], finishes: [], watches: [] },
      chartLoading: true,
      failed: false,
    };
  },

  computed: {
    range() {
      return buildRange(this.preset, this.custom);
    },
    sourceText() {
      return sourceLabel(this.video);
    },
    categoryName() {
      const match = flattenCategories(this.categoryList).find(c => c.id === this.video.category_id);
      return match ? match.name : '';
    },
    heroTitle() {
      if (this.videoLoadFailed) return 'Video not found';
      return this.video.name || 'Video analytics';
    },
    thumbStyle() {
      const url = this.video && this.video.thumbnail ? this.video.thumbnail : '';
      return url
        ? { backgroundImage: `url("${url}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {};
    },
    activeSeries() {
      return this.seriesData[this.metric] || [];
    },
    dailyRows() {
      return this.report.rows || mergeSeries(this.seriesData);
    },
    cards() {
      return buildStatCards(this.summary, formatNumber);
    },
  },

  created() {
    // One stale-response guard for the four chart calls this page fires
    // together — see src/services/analyticsApi.js's latestOnly() doc.
    this.chartGuard = latestOnly();
    this.loadVideo();
    this.loadCategories();
    this.load();
  },

  methods: {
    async loadVideo() {
      this.videoLoadFailed = false;
      try {
        this.video = await Video.$find(this.videoId);
      } catch (e) {
        this.video = {};
        this.videoLoadFailed = true;
      }
    },

    async loadCategories() {
      try {
        this.categoryList = (await Category.$get()) || [];
      } catch (e) {
        // The category chip just stays hidden without this.
      }
    },

    load() {
      this.failed = false;
      return this.loadChart();
    },

    async loadChart() {
      const done = this.chartGuard();
      this.chartLoading = true;
      try {
        const report = await fetchChart({ metric: 'all', range: this.range, videoId: this.videoId });
        if (!done()) return;
        this.report = report || {};
        this.seriesData = this.report.series || {};
        this.summary = summaryFrom(this.report.selected_tab_data);
      } catch (e) {
        if (!done()) return;
        this.failed = true;
      } finally {
        if (done()) this.chartLoading = false;
      }
    },

    exportReport() {
      downloadCsv('groovevideo-activity.csv', [['Period (US Eastern)', 'Player loads', 'Plays', 'Daily unique viewers', 'Finishes', 'Watch seconds'], ...this.dailyRows.map(row => [row.key || row.label, row.impressions, row.plays, row.visitors, row.finishes, row.watches])]);
    },
    onRangeChange({ preset, custom }) {
      this.preset = preset;
      this.custom = custom;
      this.load();
    },

    onMetricChange(metric) {
      if (metric === this.metric) return;
      this.metric = metric;
    },

    goBack() {
      this.$router.push({ name: 'analytics' });
    },

    editVideo() {
      this.$router.push({ name: 'editVideo', params: { id: this.videoId } });
    },
  },
};
</script>

<style scoped>
.gs-video-analytics {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gs-video-analytics__back {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  align-self: flex-start;
  padding: 0;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gs-video-analytics__back:hover {
  color: var(--gs-foreground);
}

.gs-video-analytics__back:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-video-analytics__hero {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  padding: 20px 24px;
}

.gs-video-analytics__hero-media {
  flex: none;
  width: 160px;
  height: 90px;
  background: var(--gs-surface-subtle);
  background-repeat: no-repeat;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-video-analytics__hero-body {
  flex: 1 1 240px;
  min-width: 0;
}

.gs-video-analytics__hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.gs-video-analytics__chip {
  display: inline-flex;
  padding: 2px 9px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.gs-video-analytics__title {
  margin: 0;
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-video-analytics__edit {
  flex: none;
}

.gs-video-analytics__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
}

.gs-analytics__eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-video-analytics__controls-title {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 700;
}

.gs-video-analytics__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
}

.gs-video-analytics__table-card {
  padding: 20px 24px 18px;
}

.gs-video-analytics__table-head {
  margin-bottom: 16px;
}

.gs-video-analytics__table-title {
  margin: 0 0 4px;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
}

.gs-video-analytics__table-subtitle {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-video-analytics__note {
  margin: 0;
}

@media (max-width: 640px) {
  .gs-video-analytics__hero,
  .gs-video-analytics__controls,
  .gs-video-analytics__table-card {
    padding: 16px 18px;
  }

  .gs-video-analytics__hero-media {
    width: 100%;
    height: 160px;
  }

  .gs-video-analytics__controls {
    align-items: flex-start;
  }
}
</style>
